import React, { useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
  ScriptableContext,
  ChartDataset
} from 'chart.js';
import style from './style.module.css';
import { FC } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SerializedError } from '@reduxjs/toolkit';
import { SelectYear } from '../../../../components/selects';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { FinancingDataItem } from '../api/types';
import { PreloaderSvg } from '../../../preloaderSvg/PreloaderSvg';
import { useTranslation } from 'react-i18next';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  ChartDataLabels
);

interface LeasingProjectsProps {
  data?: FinancingDataItem[];
  isLoading?: boolean;
  error?: FetchBaseQueryError | SerializedError;
  selectedYear: number | null;
  onYearChange: (year: number | null) => void;
}

const calculateTotal = (data: (number | null)[]): number => {
  return data.reduce<number>((sum: number, value: number | null) => sum + (value || 0), 0);
};

export const LeasingProjects: FC<LeasingProjectsProps> = ({ 
  data, 
  isLoading, 
  error, 
  selectedYear, 
  onYearChange 
}) => {
  const { t, i18n } = useTranslation();
  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstance = useRef<ChartJS | null>(null);

  useEffect(() => {
    if (!chartRef.current) return;
    if (!data || !Array.isArray(data) || data.length === 0) {
      
      return;
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const values = data.map(item => Number(item.totalFinancing) || 0);
    const total = calculateTotal(values);

    const backgroundColor = [
      'rgba(0, 100, 0, 0.8)', 
      'rgba(34, 139, 34, 0.8)', 
      'rgba(154, 205, 50, 0.8)',
      'rgba(255, 215, 0, 0.8)',   
      'rgba(255, 140, 0, 0.8)',   
      'rgba(205, 133, 63, 0.8)',  
      'rgba(144, 238, 144, 0.8)', 
      'rgba(135, 206, 235, 0.8)', 
      'rgba(147, 112, 219, 0.8)'  
    ];

    const borderColor = [
      '#006400',
      '#228B22',
      '#9ACD32',
      '#FFD700',
      '#FF8C00',
      '#CD853F',
      '#90EE90',
      '#87CEEB',
      '#9370DB'
    ];

    const chartData: ChartData<'doughnut'> = {
      labels: data.map(item => {
        
        return i18n.language === 'ky' ? item.projectNameKy : item.projectNameRu
      }),
      datasets: [{
        data: values,
        backgroundColor,
        borderColor,
        borderWidth: 1
      }]
    };

    const options: ChartOptions<'doughnut'> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          labels: {
            font: {
              size: 12
            },
            padding: 20,
            generateLabels: (chart) => {
              const dataset = chart.data.datasets[0];
              return (chart.data.labels || []).map((label, i) => {
                const value = dataset.data[i] as number;
                const percentage = ((value / total) * 100).toFixed(1);
                return {
                  text: `${label} - ${value.toLocaleString(i18n.language === 'ky' ? 'ky-KG' : 'ru-RU')} Сом (${percentage}%)`,
                  fillStyle: backgroundColor[i],
                  hidden: false,
                  lineCap: undefined,
                  lineDash: undefined,
                  lineDashOffset: undefined,
                  lineJoin: undefined,
                  lineWidth: undefined,
                  strokeStyle: borderColor[i],
                  pointStyle: undefined,
                  rotation: undefined,
                  datasetIndex: 0,
                  index: i
                };
              });
            }
          }
        },
        datalabels: {
          formatter: (value: number, ctx) => {
            const percentage = ((value / total) * 100).toFixed(1);
            return `${percentage}%`;
          },
          color: '#000',
          font: {
            weight: 'bold',
            size: 12
          },
          display: (ctx) => {
            const value = ctx.dataset.data[ctx.dataIndex] as number;
            return (value / total) > 0.05; 
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.raw as number;
              const percentage = (value / total * 100).toFixed(1);
              return ` ${value.toLocaleString(i18n.language === 'ky' ? 'ky-KG' : 'ru-RU')} ${t('currency')} (${percentage}%)`;
            }
          }
        }
      }
    };

    chartInstance.current = new ChartJS(ctx, {
      type: 'doughnut',
      data: chartData,
      options: options
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data]);

  if (isLoading) {
    return (
      <div><PreloaderSvg/></div>
    );
  }

  if (error) {
    return <div className={style.error}>{t('Error loading data')}</div>;
  }

  return (
    <div className={style.container}>
      <div className={style.header}>
        <h2 className={style.title}>{t('Leasing projects')}</h2>
        <div className={style.yearSelect}>
          <SelectYear 
            value={selectedYear}
            onChange={onYearChange}
          />
        </div>
      </div>
      <div className={style.chartContainer}>
        <canvas ref={chartRef} />
      </div>
    </div>
  );
};